import { Input } from '@fountain/fountain-ui-components';
import { Grid, InputLabel, makeStyles } from '@material-ui/core';
import React, { useEffect, useState, VFC } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { messages } from './messages';

/* eslint-disable camelcase */
export type EmploymentHistoryEntry = {
  city: string;
  state_code: string;
  state_code_iso: string;
  country_code: string;
  country_code_iso: string;
  employer_contact_name: string;
  employer_name: string;
  endMonth: string;
  endYear: string;
  active?: 'on';
  noHistory?: 'on';
  startMonth: string;
  startYear: string;
  job_title: string;
};
/* eslint-enable camelcase */

const useStyles = makeStyles(theme => ({
  p: {
    marginTop: 0,
    marginBottom: 0,
  },
  input: {
    marginTop: theme.spacing(2),
  },
  label: {
    lineHeight: theme.typography.body2.lineHeight,
    fontSize: theme.typography.body2.fontSize,
    fontFamily: theme.typography.body2.fontFamily,
    marginBottom: theme.spacing(0.5),
  },
  italics: {
    fontStyle: 'italic',
  },
}));

interface EmploymentHistoryProps {
  employmentHistory: EmploymentHistoryEntry;
  editDisabled: boolean;
  handleKeyPress: () => void;
  setValue: (newValue: EmploymentHistoryEntry, index: number) => void;
  index: number;
}

export const EmploymentHistory: VFC<EmploymentHistoryProps> = ({
  employmentHistory,
  editDisabled,
  handleKeyPress,
  setValue,
  index,
}) => {
  const classes = useStyles();
  const [data, setData] = useState<EmploymentHistoryEntry>({
    city: '',
    state_code: '',
    state_code_iso: '',
    country_code: '',
    country_code_iso: '',
    employer_contact_name: '',
    employer_name: '',
    endMonth: '',
    endYear: '',
    startMonth: '',
    startYear: '',
    job_title: '',
  });

  useEffect(() => {
    setData(employmentHistory);
  }, [employmentHistory]);

  type FieldName =
    | 'city'
    | 'state_code'
    | 'state_code_iso'
    | 'country_code'
    | 'country_code_iso'
    | 'employer_contact_name'
    | 'employer_name'
    | 'endMonth'
    | 'endYear'
    | 'active'
    | 'startMonth'
    | 'startYear'
    | 'job_title';

  const onFieldChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: FieldName,
  ) => {
    setData({
      ...data,
      [fieldName]: e.target.value,
    });

    setValue(
      {
        ...data,
        [fieldName]: e.target.value,
      },
      index,
    );
  };

  const handleChangeField =
    (fieldName: FieldName) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onFieldChange(e, fieldName);
    };

  const renderEditField = () => {
    return (
      <Grid>
        <Grid className={classes.input}>
          <InputLabel className={classes.label} htmlFor="startMonth">
            <FormattedMessage {...messages.startMonthField} />
          </InputLabel>
          <Input
            value={data.startMonth}
            onChange={handleChangeField('startMonth')}
            onKeyPress={handleKeyPress}
          />
        </Grid>
        <Grid className={classes.input}>
          <InputLabel className={classes.label} htmlFor="startYear">
            <FormattedMessage {...messages.startYearField} />
          </InputLabel>
          <Input
            value={data.startYear}
            onChange={handleChangeField('startYear')}
            onKeyPress={handleKeyPress}
          />
        </Grid>
        <Grid className={classes.input}>
          <InputLabel className={classes.label} htmlFor="endMonth">
            <FormattedMessage {...messages.endMonthField} />
          </InputLabel>
          <Input
            value={data.endMonth}
            onChange={handleChangeField('endMonth')}
            onKeyPress={handleKeyPress}
          />
        </Grid>
        <Grid className={classes.input}>
          <InputLabel className={classes.label} htmlFor="endYear">
            <FormattedMessage {...messages.endYearField} />
          </InputLabel>
          <Input
            value={data.endYear}
            onChange={handleChangeField('endYear')}
            onKeyPress={handleKeyPress}
          />
        </Grid>
        <Grid className={classes.input}>
          <InputLabel className={classes.label} htmlFor="active">
            <FormattedMessage {...messages.activeField} />
          </InputLabel>
          <Input
            value={data.active}
            onChange={handleChangeField('active')}
            onKeyPress={handleKeyPress}
          />
        </Grid>
        <Grid className={classes.input}>
          <InputLabel className={classes.label} htmlFor="employer_name">
            <FormattedMessage {...messages.employerNameField} />
          </InputLabel>
          <Input
            value={data.employer_name}
            onChange={handleChangeField('employer_name')}
            onKeyPress={handleKeyPress}
          />
        </Grid>
        <Grid className={classes.input}>
          <InputLabel className={classes.label} htmlFor="job_title">
            <FormattedMessage {...messages.jobTitleField} />
          </InputLabel>
          <Input
            value={data.job_title}
            onChange={handleChangeField('job_title')}
            onKeyPress={handleKeyPress}
          />
        </Grid>
        <Grid className={classes.input}>
          <InputLabel className={classes.label} htmlFor="country_code">
            <FormattedMessage {...messages.countryCodeField} />
          </InputLabel>
          <Input
            value={data.country_code}
            onChange={handleChangeField('country_code')}
            onKeyPress={handleKeyPress}
          />
        </Grid>
        <Grid className={classes.input}>
          <InputLabel className={classes.label} htmlFor="country_code_iso">
            <FormattedMessage {...messages.countryCodeIsoField} />
          </InputLabel>
          <Input
            value={data.country_code_iso}
            onChange={handleChangeField('country_code_iso')}
            onKeyPress={handleKeyPress}
          />
        </Grid>
        <Grid className={classes.input}>
          <InputLabel className={classes.label} htmlFor="city">
            <FormattedMessage {...messages.cityField} />
          </InputLabel>
          <Input
            value={data.city}
            onChange={handleChangeField('city')}
            onKeyPress={handleKeyPress}
          />
        </Grid>
        <Grid className={classes.input}>
          <InputLabel className={classes.label} htmlFor="state_code">
            <FormattedMessage {...messages.stateCodeField} />
          </InputLabel>
          <Input
            value={data.state_code}
            onChange={handleChangeField('state_code')}
            onKeyPress={handleKeyPress}
          />
        </Grid>
        <Grid className={classes.input}>
          <InputLabel className={classes.label} htmlFor="state_code_iso">
            <FormattedMessage {...messages.stateCodeIsoField} />
          </InputLabel>
          <Input
            value={data.state_code_iso}
            onChange={handleChangeField('state_code_iso')}
            onKeyPress={handleKeyPress}
          />
        </Grid>
      </Grid>
    );
  };

  const formatDateRange = (
    startMonth: string,
    startYear: string,
    endMonth: string,
    endYear: string,
    active?: 'on',
  ) => {
    const startDate = new Date(
      parseInt(startYear, 10),
      parseInt(startMonth, 10) - 1,
    );
    const endDate =
      active === 'on'
        ? null
        : new Date(parseInt(endYear, 10), parseInt(endMonth, 10) - 1);

    return (
      <>
        <FormattedDate value={startDate} year="numeric" month="short" /> -{' '}
        {active === 'on' ? (
          <FormattedMessage {...messages.present} />
        ) : endDate ? (
          <FormattedDate value={endDate} year="numeric" month="short" />
        ) : (
          ''
        )}
      </>
    );
  };

  const renderValue = () => {
    const dateRange = formatDateRange(
      data.startMonth,
      data.startYear,
      data.endMonth,
      data.endYear,
      data.active,
    );

    const hasEmployerDetails = data.noHistory !== 'on';

    return (
      <p className={classes.p}>
        <span className={classes.label}>
          {hasEmployerDetails ? (
            <>
              {dateRange} | {data.employer_name} {data.job_title} {data.city},{' '}
              {data.state_code} {data.country_code_iso}.{' '}
              <span className={classes.italics}>
                <FormattedMessage {...messages.employerContactNameField} />:{' '}
                {data.employer_contact_name}
              </span>
            </>
          ) : (
            <>
              {dateRange} | <FormattedMessage {...messages.noEmployerDetails} />
            </>
          )}
        </span>
        <br />
      </p>
    );
  };
  return editDisabled ? renderValue() : renderEditField();
};

export default EmploymentHistory;
